import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { imgSrc, shopTryon } from "./repApi";

/* নিজের উপর দেখা — ছবি দিন বা ক্যামেরা; চিত্রা AI-তে পাঞ্জাবি বসিয়ে দেন */
export default function TryOn({ open, product, panjabis, results, onResult, onReset, onSwitch, onClose, onCompare, onSave, onOrder, saved }) {
  const [photo, setPhoto] = useState(null);
  const [camOn, setCamOn] = useState(false);
  const [camErr, setCamErr] = useState("");
  const [busyFor, setBusyFor] = useState(null);
  const [error, setError] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  const current = product || panjabis[0];
  const resultUrl = current ? results[current.id] : null;

  // ক্যামেরার স্রোত ঘরে ফেরা
  useEffect(
    () => () => {
      try {
        streamRef.current?.getTracks().forEach((t) => t.stop());
      } catch {}
    },
    [],
  );

  const stopCam = () => {
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {}
    streamRef.current = null;
    setCamOn(false);
  };

  const readPhoto = (dataUrl) => {
    setPhoto(dataUrl);
    setCamErr("");
    stopCam();
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 900;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const cv = document.createElement("canvas");
        cv.width = Math.round(img.width * scale);
        cv.height = Math.round(img.height * scale);
        cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
        readPhoto(cv.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
  };

  const startCam = async () => {
    setCamErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 720 } } });
      streamRef.current = stream;
      setCamOn(true);
      setTimeout(() => videoRef.current && (videoRef.current.srcObject = stream), 30);
    } catch {
      setCamErr("ক্যামেরা খুলল না — বাঁ দিকের ছবি-দেওয়ার পথ ব্যবহার করুন।");
    }
  };

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const cv = document.createElement("canvas");
    cv.width = 720;
    cv.height = 900;
    cv.getContext("2d").drawImage(v, 0, 0, 720, 900);
    readPhoto(cv.toDataURL("image/jpeg", 0.85));
  };

  const generate = async (pid) => {
    if (!photo || busyFor || results[pid]) return;
    setBusyFor(pid);
    setError("");
    try {
      const url = await shopTryon(pid, photo);
      onResult(pid, url);
    } catch (e) {
      setError(e.message || "এবার সাজানো গেল না");
    }
    setBusyFor(null);
  };

  const photoRef = useRef(null);
  photoRef.current = photo;
  const busyRef = useRef(null);
  busyRef.current = busyFor;
  const resultsRef = useRef(null);
  resultsRef.current = results;

  useEffect(() => {
    if (photoRef.current && current && !resultsRef.current[current.id] && !busyRef.current) generate(current.id);
    // eslint-disable-next-line
  }, [photo, current?.id]);

  if (!open || !current) return null;

  const switchTo = (p) => {
    if (busyFor) return;
    if (!results[p.id]) generate(p.id);
    onSwitch?.(p);
  };

  const idx = panjabis.findIndex((p) => p.id === current.id);
  const prev = panjabis[(idx - 1 + panjabis.length) % panjabis.length];
  const next = panjabis[(idx + 1) % panjabis.length];
  const doneCount = panjabis.filter((p) => results[p.id]).length;
  const isSaved = saved.includes(current.id);

  return (
    <motion.div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-dusk/92 p-3 backdrop-blur-md sm:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="tryon-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-gold/25 bg-plaster p-5 sm:p-7"
      >
        <button
          data-testid="tryon-close"
          onClick={() => { stopCam(); onClose(); }}
          className="absolute right-4 top-4 z-10 rounded-full bg-dusk/70 p-2 text-sand/70 transition-colors hover:text-sand"
          aria-label="বন্ধ করুন"
        >
          <X size={16} />
        </button>
        <h3 className="font-display text-2xl text-sand sm:text-3xl">নিজের উপর দেখে নিন</h3>
        <p className="mt-1 text-xs text-sand/55">ছবি শুধু দেখার জন্য — চিত্রা কোথাও পাঠান না।</p>

        {/* পোশাক-বদলের সারি */}
        <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1" data-testid="tryon-switcher">
          {panjabis.map((p) => (
            <button
              key={p.id}
              data-testid={`tryon-switch-${p.id}`}
              onClick={() => switchTo(p)}
              className={`relative h-14 w-12 shrink-0 overflow-hidden rounded-lg border-2 transition-all duration-300 ${
                current.id === p.id ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"
              }`}
              aria-label={p.title}
            >
              <img src={imgSrc(p.img)} alt={p.title} className="h-full w-full object-cover" />
              {busyFor === p.id && (
                <span className="absolute inset-0 flex items-center justify-center bg-dusk/60">
                  <span className="h-3 w-3 animate-flicker rounded-full bg-vermilion" />
                </span>
              )}
              {results[p.id] && busyFor !== p.id && (
                <span className="absolute bottom-0.5 right-0.5 h-2 w-2 rounded-full bg-gold ring-1 ring-dusk" />
              )}
            </button>
          ))}
        </div>

        {!photo ? (
          <div className="mt-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <button
                data-testid="tryon-upload"
                onClick={() => fileRef.current?.click()}
                className="flex flex-col items-center gap-2 rounded-xl border border-gold/25 bg-earthen/60 py-8 text-sm text-sand/85 transition-colors hover:border-gold/60 hover:text-gold"
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                </svg>
                ছবি দিন
                <span className="text-[10px] text-sand/45">আপনার ফোন বা কম্পিউটার থেকে</span>
              </button>
              <button
                data-testid="tryon-camera"
                onClick={startCam}
                className="flex flex-col items-center gap-2 rounded-xl border border-gold/25 bg-earthen/60 py-8 text-sm text-sand/85 transition-colors hover:border-gold/60 hover:text-gold"
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2Z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                ক্যামেরা খুলুন
                <span className="text-[10px] text-sand/45">এখনই ছবি তুলুন</span>
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="tryon-file-input" />
            {camErr && <p className="mt-3 text-xs text-vermilion" data-testid="tryon-cam-error">{camErr}</p>}
          </div>
        ) : (
          <div className="mt-5">
            {camOn ? (
              <div className="mx-auto max-w-sm">
                <video ref={videoRef} autoPlay playsInline className="w-full rounded-xl" data-testid="tryon-cam-preview" />
                <div className="mt-3 flex justify-center gap-3">
                  <button data-testid="tryon-capture" onClick={capture} className="rounded-full bg-alpana px-6 py-2.5 text-sm font-semibold text-dusk transition-transform duration-300 hover:scale-105">
                    ছবি তুলুন
                  </button>
                  <button data-testid="tryon-cam-cancel" onClick={() => { stopCam(); setPhoto(null); }} className="rounded-full border border-sand/25 px-5 py-2.5 text-sm text-sand/80">
                    বাতিল
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid items-center gap-5 sm:grid-cols-2">
                <div className="relative">
                  {resultUrl ? (
                    <img src={imgSrc(resultUrl)} alt="আপনার উপর পাঞ্জাবি" data-testid="tryon-result" className="w-full rounded-xl border border-gold/30" />
                  ) : (
                    <div className="flex aspect-[3/4] w-full items-center justify-center rounded-xl border border-gold/25 bg-earthen/60" data-testid="tryon-waiting">
                      <div className="px-6 text-center">
                        <span className="mx-auto block h-4 w-4 animate-flicker rounded-full bg-vermilion" />
                        <p className="mt-3 text-sm text-sand/80">চিত্রা সাজিয়ে আনছেন…</p>
                        <p className="mt-1 text-[11px] leading-relaxed text-sand/50">
                          হাতে-আঁকা পাঞ্জাবিটা আপনার ছবিতে বসাতে এক মিনিটের মতো লাগবে
                        </p>
                      </div>
                    </div>
                  )}
                  <img src={photo} alt="আপনার ছবি" className="absolute bottom-2 left-2 h-14 w-14 rounded-lg border border-gold/40 object-cover" />
                </div>
                <div>
                  <p className="font-display text-xl text-sand">{current.title}</p>
                  <p className="mt-1 text-xs text-sand/55">{current.craft}</p>
                  <p className="mt-2 font-display text-lg text-gold">{current.price}</p>
                  {busyFor && <p className="mt-2 text-xs text-gold/80">এই পাঞ্জাবিটা সাজানো হচ্ছে…</p>}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      data-testid="tryon-prev"
                      onClick={() => switchTo(prev)}
                      className="rounded-full border border-sand/25 px-4 py-2 text-xs text-sand/80 transition-colors hover:border-gold hover:text-gold"
                    >
                      ← আগেরটা
                    </button>
                    <button
                      data-testid="tryon-next"
                      onClick={() => switchTo(next)}
                      className="rounded-full border border-sand/25 px-4 py-2 text-xs text-sand/80 transition-colors hover:border-gold hover:text-gold"
                    >
                      পরেরটা →
                    </button>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      data-testid="tryon-compare-btn"
                      onClick={onCompare}
                      disabled={doneCount < 2}
                      className="rounded-full border border-gold/40 px-4 py-2 text-xs text-gold transition-colors hover:bg-gold hover:text-dusk disabled:opacity-35"
                    >
                      পাশাপাশি দেখুন {doneCount < 2 ? `(${doneCount}/২ সাজা হয়েছে)` : ""}
                    </button>
                    <button
                      data-testid="tryon-save"
                      onClick={() => onSave(current.id)}
                      className={`rounded-full px-4 py-2 text-xs transition-all duration-300 ${
                        isSaved ? "bg-vermilion text-alpana" : "border border-gold/40 text-gold hover:bg-gold hover:text-dusk"
                      }`}
                    >
                      {isSaved ? "পছন্দ জমা আছে" : "পছন্দে রাখুন"}
                    </button>
                    <button
                      data-testid="tryon-order"
                      onClick={() => onOrder(current)}
                      className="rounded-full bg-alpana px-4 py-2 text-xs font-medium text-dusk transition-transform duration-300 hover:scale-105"
                    >
                      অর্ডার করুন
                    </button>
                  </div>
                  <button
                    data-testid="tryon-change-photo"
                    onClick={() => { setPhoto(null); onReset?.(); }}
                    className="mt-4 block text-[11px] text-sand/45 underline-offset-2 hover:text-gold hover:underline"
                  >
                    অন্য ছবি দিই
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {error && <p className="mt-3 text-xs text-vermilion" data-testid="tryon-error">{error}</p>}
      </motion.div>
    </motion.div>
  );
}
